import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CaretDown, CaretUp, Ticket, Pause, Play, Trash, PencilSimple, Check, X, Copy, ChatText } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (c) => {
  const v = (c || 0) / 100;
  return `$${v.toLocaleString("en-CA", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
};
const empty = { code: "", discount_type: "percent", value: "", expires_on: "", max_uses: "" };

export function PromoCodesCard() {
  const [open, setOpen] = useState(false);
  const [textFor, setTextFor] = useState(null);
  const [textPhone, setTextPhone] = useState("");
  const [textConsent, setTextConsent] = useState(false);
  const [consentOnFile, setConsentOnFile] = useState(null);

  useEffect(() => {
    const digits = (textPhone || "").replace(/\D/g, "");
    if (digits.length < 10) { setConsentOnFile(null); return; }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/sms-consent?phone=${encodeURIComponent(textPhone)}`);
        if (live) setConsentOnFile(data.on_file ? (data.since || true) : false);
      } catch { if (live) setConsentOnFile(false); }
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [textPhone]);
  const [textBusy, setTextBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(empty);

  const startEdit = (c) => {
    setEditId(c.id);
    setEdit({
      code: c.code,
      discount_type: c.discount_type || "percent",
      value: (c.discount_type || "percent") === "fixed" ? ((c.discount_cents || 0) / 100).toString() : String(Math.round(c.discount_percent || 0)),
      expires_on: c.expires_on || "",
      max_uses: c.max_uses ? String(c.max_uses) : "",
      applies: c.applies_to_item_ids || [],
    });
    if (fdItems === null) api.get("/admin/front-desk/items").then((r) => setFdItems(r.data || [])).catch(() => setFdItems([]));
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/promo-codes/${editId}`, {
        code: edit.code.trim(),
        discount_type: edit.discount_type,
        discount_percent: edit.discount_type === "percent" ? Math.round(parseFloat(edit.value)) : null,
        discount_cents: edit.discount_type === "fixed" ? Math.round(parseFloat(edit.value) * 100) : null,
        expires_on: edit.expires_on || null,
        max_uses: edit.max_uses ? parseInt(edit.max_uses, 10) : null,
        applies_to_item_ids: edit.applies?.length ? edit.applies : null,
      });
      setEditId(null);
      await load();
      toast.success("Coupon updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const load = () => api.get("/admin/promo-codes").then((r) => setCodes(r.data)).catch((e) => toast.error(formatApiError(e)));
  const [fdItems, setFdItems] = useState(null);
  useEffect(() => { if (open && !codes) load(); }, [open]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = {
        code: form.code.trim(),
        discount_type: form.discount_type,
        discount_percent: form.discount_type === "percent" ? Math.round(parseFloat(form.value)) : null,
        discount_cents: form.discount_type === "fixed" ? Math.round(parseFloat(form.value) * 100) : null,
        expires_on: form.expires_on || null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
      };
      await api.post("/admin/promo-codes", body);
      setForm(empty);
      await load();
      toast.success(`Code ${body.code} created`);
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (c) => {
    if (c.active && !window.confirm(`Deactivate code "${c.code}"? It will stop working immediately — you can reactivate it any time.`)) return;
    try { await api.patch(`/admin/promo-codes/${c.id}`, { active: !c.active }); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const hardDelete = async (c) => {
    if (!window.confirm(`Permanently DELETE code "${c.code}"? This removes it and its usage history — this cannot be undone. (Use Deactivate if you just want to turn it off.)`)) return;
    try { await api.delete(`/admin/promo-codes/${c.id}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card" data-testid="promo-codes-card">
      <button type="button" className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer p-0" onClick={() => setOpen((o) => !o)} data-testid="promo-codes-toggle">
        <div className="flex items-center gap-2">
          <Ticket size={22} weight="duotone" color="#DB2777" />
          <div className="text-left">
            <h3 className="text-lg font-bold font-display">Promo codes / coupons</h3>
            <p className="text-xs text-[#000000]">Percent or fixed-amount discounts for public checkout — with optional expiry date and usage cap</p>
          </div>
        </div>
        {open ? <CaretUp size={18} color="#52525B" /> : <CaretDown size={18} color="#52525B" />}
      </button>
      {open && (
        <div className="mt-4 flex flex-col gap-4">
          <form onSubmit={create} className="grid grid-cols-12 gap-2 items-end">
            <label className="col-span-6 sm:col-span-2 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Code
              <input data-testid="promo-code-input" className="input-base" required minLength={3} placeholder="SPRING50" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
            </label>
            <label className="col-span-6 sm:col-span-2 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Type
              <select data-testid="promo-type" className="input-base normal-case tracking-normal" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}>
                <option value="percent">% off</option>
                <option value="fixed">$ off</option>
              </select>
            </label>
            <label className="col-span-6 sm:col-span-2 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">{form.discount_type === "percent" ? "Percent" : "Amount ($)"}
              <input data-testid="promo-value" className="input-base text-right" required type="number"
                min={form.discount_type === "percent" ? "1" : "0.01"} step={form.discount_type === "percent" ? "1" : "0.01"}
                placeholder={form.discount_type === "percent" ? "20" : "25"}
                value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </label>
            <label className="col-span-6 sm:col-span-2 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Expires (optional)
              <input data-testid="promo-expires" className="input-base" type="date" value={form.expires_on} onChange={(e) => setForm({ ...form, expires_on: e.target.value })} />
            </label>
            <label className="col-span-6 sm:col-span-2 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Max uses (optional)
              <input data-testid="promo-max-uses" className="input-base text-right" type="number" min="1" placeholder="∞" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
            </label>
            <div className="col-span-6 sm:col-span-2">
              <button type="submit" className="btn-primary text-sm w-full" disabled={busy} data-testid="promo-create">Create code</button>
            </div>
          </form>

          {codes && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="promo-codes-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.1em] text-[#000000] border-b border-[#C4C8CE]">
                    <th className="px-2 py-1">Code</th><th className="px-2 py-1">Discount</th>
                    <th className="px-2 py-1 text-right">Uses</th><th className="px-2 py-1">Expires</th>
                    <th className="px-2 py-1">Issued by</th><th className="px-2 py-1">Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => {
                    const expired = c.expires_on && c.expires_on < new Date().toISOString().slice(0, 10);
                    const usedUp = c.max_uses && (c.uses_count || 0) >= c.max_uses;
                    if (editId === c.id) return (
                      <tr key={c.id} className="border-b border-[#ECEDEF] bg-[#F7FBEF]" data-testid={`promo-edit-row-${c.code}`}>
                        <td className="px-2 py-1.5">
                          <input className="input-base text-sm w-28 font-mono" value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value })} data-testid="promo-edit-code" />
                        </td>
                        <td className="px-2 py-1.5">
                          <span className="inline-flex items-center gap-1">
                            <select className="input-base text-sm w-20" value={edit.discount_type} onChange={(e) => setEdit({ ...edit, discount_type: e.target.value })} data-testid="promo-edit-type">
                              <option value="percent">% off</option>
                              <option value="fixed">$ off</option>
                            </select>
                            <input className="input-base text-sm w-20 text-right" type="number"
                              min={edit.discount_type === "percent" ? "1" : "0.01"} step={edit.discount_type === "percent" ? "1" : "0.01"}
                              value={edit.value} onChange={(e) => setEdit({ ...edit, value: e.target.value })} data-testid="promo-edit-value" />
                          </span>
                          <details className="mt-1.5" data-testid="promo-edit-scope">
                            <summary className="cursor-pointer text-[11px] font-bold select-none">
                              Applies to: {edit.applies?.length ? `${edit.applies.length} product${edit.applies.length > 1 ? "s" : ""}` : "all products"}
                            </summary>
                            <div className="flex flex-col gap-0.5 mt-1 max-h-40 overflow-y-auto">
                              <label className="text-[11px] flex items-center gap-1.5 cursor-pointer font-bold">
                                <input type="checkbox" checked={!edit.applies?.length}
                                  onChange={() => setEdit({ ...edit, applies: [] })} data-testid="promo-scope-all" />
                                All products
                              </label>
                              {(fdItems || []).map((it) => (
                                <label key={it.id} className="text-[11px] flex items-center gap-1.5 cursor-pointer">
                                  <input type="checkbox" checked={(edit.applies || []).includes(it.id)}
                                    data-testid={`promo-scope-${it.id}`}
                                    onChange={(e) => setEdit({ ...edit, applies: e.target.checked
                                      ? [...(edit.applies || []), it.id]
                                      : (edit.applies || []).filter((x) => x !== it.id) })} />
                                  {it.name}
                                </label>
                              ))}
                            </div>
                          </details>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input className="input-base text-sm w-16 text-right" type="number" min="1" placeholder="∞"
                            title="Max uses (blank = unlimited)"
                            value={edit.max_uses} onChange={(e) => setEdit({ ...edit, max_uses: e.target.value })} data-testid="promo-edit-max-uses" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input className="input-base text-sm" type="date" value={edit.expires_on} onChange={(e) => setEdit({ ...edit, expires_on: e.target.value })} data-testid="promo-edit-expires" />
                        </td>
                        <td className="px-2 py-1.5" colSpan={3}>
                          <span className="inline-flex items-center gap-1.5">
                            <button type="button" className="btn-primary text-xs px-3 py-1" disabled={busy} onClick={saveEdit} data-testid="promo-edit-save">
                              <Check size={12} weight="bold" className="inline mr-0.5" /> Save
                            </button>
                            <button type="button" className="btn-ghost text-xs" onClick={() => setEditId(null)} data-testid="promo-edit-cancel">
                              <X size={12} weight="bold" className="inline mr-0.5" /> Cancel
                            </button>
                          </span>
                        </td>
                      </tr>
                    );
                    return (
                      <tr key={c.id} className="border-b border-[#ECEDEF]" data-testid={`promo-row-${c.code}`}>
                        <td className="px-2 py-1.5 font-mono font-bold">
                          <span className="inline-flex items-center gap-1.5">
                            {c.code}
                            <button type="button" className="bg-transparent border-0 cursor-pointer p-0.5 text-[#52525B] hover:text-black"
                              title="Copy code" data-testid={`promo-copy-${c.code}`}
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(c.code);
                                  toast.success(`Copied ${c.code}`);
                                } catch {
                                  const ta = document.createElement("textarea");
                                  ta.value = c.code; document.body.appendChild(ta); ta.select();
                                  document.execCommand("copy"); ta.remove();
                                  toast.success(`Copied ${c.code}`);
                                }
                              }}>
                              <Copy size={14} />
                            </button>
                          </span>
                        </td>
                        <td className="px-2 py-1.5 font-semibold">
                          {(c.discount_type || "percent") === "fixed" ? `${fmt(c.discount_cents)} off` : `${Math.round(c.discount_percent)}% off`}
                          <div className="text-[10px] font-normal text-[#71717A]" data-testid={`promo-scope-label-${c.code}`}>
                            {c.applies_to_item_ids?.length ? `${c.applies_to_item_ids.length} product${c.applies_to_item_ids.length > 1 ? "s" : ""} only` : "all products"}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right">{c.uses_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                        <td className="px-2 py-1.5">{c.expires_on || "never"}</td>
                        <td className="px-2 py-1.5 text-xs text-[#000000]">{c.owner_name || "—"}</td>
                        <td className="px-2 py-1.5">
                          <span className="chip text-[10px] font-bold" style={!c.active ? { background: "#F4F4F5", color: "#000000" } : expired ? { background: "#FEE2E2", color: "#B91C1C" } : usedUp ? { background: "#FEF3C7", color: "#92400E" } : { background: "#DCFCE7", color: "#166534" }}>
                            {!c.active ? "inactive" : expired ? "expired" : usedUp ? "used up" : "active"}
                          </span>
                        </td>
                        <td className="px-2 py-1.5">
                          <span className="inline-flex items-center gap-1">
                          <button type="button" className="btn-ghost p-1" title="Edit this coupon"
                            onClick={() => startEdit(c)} data-testid={`promo-edit-${c.code}`}>
                            <PencilSimple size={14} color="#3F6212" />
                          </button>
                          {c.active ? (
                            <button type="button" className="btn-ghost text-[10px] font-bold px-2 py-1 rounded-full border border-[#FCA5A5] text-[#B91C1C] bg-white hover:bg-[#FEF2F2]"
                              title="Turn this code off (keeps its history)" onClick={() => toggleActive(c)} data-testid={`promo-deactivate-${c.code}`}>
                              <Pause size={11} weight="fill" className="inline mr-0.5" /> Deactivate
                            </button>
                          ) : (
                            <button type="button" className="btn-ghost text-[10px] font-bold px-2 py-1 rounded-full border border-[#86EFAC] text-[#166534] bg-white hover:bg-[#F0FDF4]"
                              title="Turn this code back on" onClick={() => toggleActive(c)} data-testid={`promo-reactivate-${c.code}`}>
                              <Play size={11} weight="fill" className="inline mr-0.5" /> Reactivate
                            </button>
                          )}
                          <button type="button" className="btn-ghost p-1" title="Text this code to a customer"
                            onClick={() => { setTextFor(textFor === c.id ? null : c.id); setTextPhone(""); setTextConsent(false); }} data-testid={`promo-text-${c.code}`}>
                            <ChatText size={14} color="#0A6B7A" />
                          </button>
                          <button type="button" className="btn-ghost p-1" title="Delete permanently (cannot be undone)"
                            onClick={() => hardDelete(c)} data-testid={`promo-delete-${c.code}`}>
                            <Trash size={14} color="#B91C1C" />
                          </button>
                          </span>
                          {textFor === c.id && (
                            <form className="flex flex-col gap-1.5 mt-1.5" onSubmit={async (e) => {
                              e.preventDefault();
                              setTextBusy(true);
                              try {
                                await api.post(`/admin/promo-codes/${c.id}/text`, { phone: textPhone, consent: textConsent });
                                toast.success(`Code ${c.code} texted to ${textPhone}`);
                                setTextFor(null);
                              } catch (err) { toast.error(formatApiError(err)); }
                              finally { setTextBusy(false); }
                            }}>
                              <div className="flex items-center gap-1.5">
                                <input className="input-base text-xs py-1 w-36" type="tel" required placeholder="403-555-0123"
                                  value={textPhone} onChange={(e) => setTextPhone(e.target.value)} data-testid={`promo-text-phone-${c.code}`} autoFocus />
                                <button type="submit" className="btn-primary text-xs px-2.5 py-1" disabled={textBusy || (!textConsent && !consentOnFile)} data-testid={`promo-text-send-${c.code}`}>
                                  {textBusy ? "Sending…" : "Send SMS"}
                                </button>
                              </div>
                              {consentOnFile ? (
                                <div className="text-[10px] text-[#15803D] font-semibold" data-testid={`promo-consent-onfile-${c.code}`}>
                                  ✓ SMS opt-in on file{typeof consentOnFile === "string" ? ` since ${consentOnFile.slice(0, 10)}` : ""} — no confirmation needed
                                </div>
                              ) : (
                              <label className="flex items-start gap-1.5 text-[10px] leading-tight max-w-[260px] cursor-pointer">
                                <input type="checkbox" className="mt-0.5" checked={textConsent} onChange={(e) => setTextConsent(e.target.checked)} data-testid={`promo-text-consent-${c.code}`} />
                                <span>This person is an existing customer or asked to receive this code (required by Canada's anti-spam law)</span>
                              </label>
                              )}
                            </form>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {codes.length === 0 && <tr><td colSpan={7} className="px-2 py-3 text-center italic text-[#000000]">No promo codes yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
